import { Link, useParams } from 'react-router';
import { api } from '../../shared/api/client';
import type { PublicationDetail, PublicationItem, PublicSite } from '../../shared/api/types';
import { ErrorState, Loading, Logo } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { PublicChurchSite } from '../site/PublicChurchSite';
import { VideoEmbed } from '../site/VideoEmbed';
import { PublicationArticle } from './PublicationsPage';
import { useSession } from '../session/SessionProvider';

/** Página pública de una iglesia (sin sesión): su sitio completo y lo publicado en el canal público. */
export function PublicChurchPage() {
  const { slug = '', publicationId } = useParams();
  const signedIn = useSession().state.status === 'authenticated';
  const site = useAsync((signal) => api.get<PublicSite>(`/multichurch/public/${slug}/site`, signal), [slug]);
  const feed = useAsync((signal) => api.get<{ church: { name: string }; items: PublicationItem[] }>(`/publishing/public/${slug}`, signal), [slug]);
  const one = useAsync(
    (signal) => (publicationId ? api.get<{ church: { name: string }; publication: PublicationDetail }>(`/publishing/public/${slug}/${publicationId}`, signal) : Promise.resolve(null)),
    [slug, publicationId],
  );
  const name = site.status === 'ready' ? site.data.name : feed.status === 'ready' ? feed.data.church.name : '';

  return (
    <div className="landing">
      <header className="landing__bar">
        <Link to="/" aria-label="KEORA">
          <Logo />
        </Link>
        <nav className="landing__nav" aria-label="Acceso">
          <Link className="btn btn--ghost" to="/iglesias">
            Otras iglesias
          </Link>
          {signedIn ? (
            <Link className="btn btn--ghost" to="/app">
              Ir a mi inicio
            </Link>
          ) : (
            <Link className="btn btn--ghost" to="/entrar">
              Iniciar sesión
            </Link>
          )}
        </nav>
      </header>
      <main className={publicationId ? 'public' : 'public public--wide'}>
        {publicationId ? (
          <>
            <p className="small">
              <Link to={`/i/${slug}`}>← {name || 'Inicio'}</Link>
            </p>
            {one.status === 'loading' ? <Loading /> : null}
            {one.status === 'error' ? <ErrorState error={one.error} /> : null}
            {one.status === 'ready' && one.data ? (
              <>
                {one.data.publication.videoUrl ? <VideoEmbed url={one.data.publication.videoUrl} title={one.data.publication.title} /> : null}
                {one.data.publication.series ? <p className="eyebrow">Serie: {one.data.publication.series.title}</p> : null}
                <PublicationArticle publication={one.data.publication} />
              </>
            ) : null}
          </>
        ) : (
          <>
            {site.status === 'loading' ? <Loading /> : null}
            {site.status === 'error' ? <ErrorState error={site.error} /> : null}
            {site.status === 'ready' ? <PublicChurchSite slug={slug} site={site.data} feed={feed.status === 'ready' ? feed.data.items : []} /> : null}
          </>
        )}
      </main>
    </div>
  );
}
